import type { AppConfig } from '@agnivo/app-core';
import { getTelegramWebApp } from './telegram';

/**
 * 'telegram' = the real surface path (exchange initData → session, ADR-082).
 * 'mock' = standalone dev with no Telegram host / no gateway.
 */
export type AuthMode = 'mock' | 'telegram';

/**
 * Read a build-time override, falling back to the dev default — where UNSET and
 * EMPTY both mean "not configured". Same helper `apps/web-ui/src/config.ts` defines
 * for the same reason (architect review, `web!655`): Docker injects every declared
 * ARG into subsequent RUN steps as an env var, including a prod-override ARG whose
 * default is an empty string, so a plain `??` sees `""` (not `undefined`) and does
 * NOT fall through — see that file's comment for the full mechanism. Duplicated
 * rather than shared: a three-line function is not worth a new cross-app import for.
 */
function envOrDefault(value: string | undefined, devDefault: string): string {
  return value === undefined || value === '' ? devDefault : value;
}

/**
 * ⭐ Declared BEFORE `appConfig`, not just above `oidcConfig`-equivalent code —
 * `routeViewAnalytics()` below reads this same const rather than re-deriving it,
 * and `appConfig`'s object literal calls that function eagerly as one of its own
 * properties. A `const` declared after the point it is read throws at module-eval
 * time (temporal dead zone), so the ordering here is load-bearing, not cosmetic
 * (same trap `apps/web-ui/src/config.ts` documents for the same reason).
 */
export const authMode = (import.meta.env.VITE_AUTH_MODE ?? 'mock') as AuthMode;

/**
 * mini-app runtime config, read from its OWN Vite env (app-core ships compiled
 * and can't read import.meta.env — the shell injects config, ADR-079).
 *
 * Defaults keep the shell runnable with zero backend (mock transport + mock
 * auth) so `pnpm dev` works outside Telegram. The live Telegram path activates
 * per env once the gateway initData→session endpoint lands (developer's unit 3,
 * ADR-082) — set VITE_API_MODE=live + VITE_AUTH_MODE=telegram there.
 */
export const appConfig: AppConfig = {
  apiMode: (import.meta.env.VITE_API_MODE ?? 'mock') as AppConfig['apiMode'],
  /**
   * The Telegram WebView. This is the declaration whose ABSENCE was the bug:
   * ChatScreen hardcoded `viewSurface="web"`, so ThreeStateFooter's `telegram`
   * branches were unreachable from the only shell that should reach them.
   */
  surface: 'telegram',
  // ADR-141 D1.2 analytics identity — set explicitly, never derived from `surface`.
  clientShell: 'mini-app',
  /** ADR-072 domain map: api.dev.agnivo.org (dev) / api.agnivo.org (prod). */
  apiBaseUrl:
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://api.dev.agnivo.org',
  /**
   * Report the Telegram theme (light|dark) on every live /v1 request so the
   * gateway metric `gateway_miniapp_color_scheme_total{scheme}` can size the
   * audience's light/dark split (product ask 2026-07-14 — turns "audience skews
   * dark" from an assumption into a number). Read per-request so a mid-session
   * theme switch is reflected. Only present inside Telegram (getTelegramWebApp()
   * null in standalone/mock → no header). Gateway CORS allows X-Color-Scheme
   * (gateway!142); metricsMW reads it HTTP-layer-only, never forwarded to gRPC.
   */
  /**
   * ADR-092 flip (choreography step: after the gateway legs + DS flag are
   * live on staging — developer ping 2026-07-18): carry the addressed
   * agent_id on send/:stream/reads. Prod carry-condition: the ADR-012
   * cutover checklist must include "gateway ADR-092 legs promoted" before
   * prod CLIENT_REST exposure (mini-app is dark on prod until then).
   */
  sendAgentId: true,
  /**
   * Voice P2.2 (ADR-094) — capture ON, restored 2026-07-21 evening. The morning
   * OFF was built on a WRONG diagnosis ("WebView records unplayable blobs");
   * the decode-probe (Settings 5-tap Diagnostics) met the codified re-enable
   * bar with device ground truth: Dmitry's Android `audio/webm 0.66s 48kHz
   * peak 0.068` and desktop `peak 0.698 (spoken)` — capture is FINE. The real
   * bug was the `<audio src=blob:>` PLAYER (doesn't play in TG WebViews) →
   * replaced with the Web-Audio VoiceClipPlayer. Product acked the reversal
   * (decision doc 2026-07-21 supersession). The bot-chat fallback seam below
   * stays wired — the hint only renders where capture is actually unavailable.
   */
  voiceCapture: true,
  /**
   * Where voice DOES work — the bot chat this mini-app fronts. Default = the
   * staging bot (same default-to-dev rule as apiBaseUrl above); prod env sets
   * VITE_BOT_CHAT_URL=https://t.me/agnivo_ai_bot. No ?start payload yet: the
   * deep-link "land in the SAME conversation/agent context" contract is an open
   * architect question — env-overridable so the answer needs no code change.
   */
  voiceFallbackUrl:
    (import.meta.env.VITE_BOT_CHAT_URL as string | undefined) ?? 'https://t.me/agnivo_dev_ai_bot',
  /** t.me links must switch to the chat NATIVELY — openTelegramLink is the only
   *  guaranteed path inside the WebView; window.open covers standalone/mock dev
   *  and clients predating the API. */
  openExternal: (url) => {
    const tg = getTelegramWebApp();
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank', 'noreferrer');
  },
  requestHeaders: () => {
    const cs = getTelegramWebApp()?.colorScheme;
    return cs ? { 'x-color-scheme': cs } : undefined;
  },
  /**
   * ADR-141 D1.1 — the Umami collector, and the switch that arms a route-view
   * sender on this surface. `undefined` unless BOTH values are configured.
   *
   * ⛔ NO DEV DEFAULT, unlike every other field above, and the asymmetry is the
   * point (same rule `apps/web-ui/src/config.ts` documents for its own
   * `routeViewAnalytics()`): a missing analytics collector breaks nothing — it
   * just means no telemetry — so a default here would make a build that nobody
   * configured start POSTing to a guessed origin, and a wrong website id writes
   * rows into someone else's dataset where they are indistinguishable from real
   * ones. Missing-and-silent beats configured-and-wrong.
   *
   * ⚠️ Umami's `website` records are per-DEPLOYED-HOSTNAME
   * (`reports/sre_agent/2026-09-12-incident-umami-zero-class-hostname-violation.md`:
   * web-ui's website id is scoped to `app.dev.agnivo.org` specifically) — mini-app
   * deploys to a DIFFERENT host (`miniapp.dev.agnivo.org`,
   * `deploy/helm/values-cloud-dev.yaml`), so it needs its OWN website id from SRE,
   * never web-ui's. Until SRE provisions one, `VITE_UMAMI_WEBSITE_ID` stays unset
   * and this field stays `undefined` — the config wiring is complete and safe to
   * ship ahead of that provisioning.
   */
  analytics: routeViewAnalytics(),
};

function routeViewAnalytics(): AppConfig['analytics'] {
  const collectorUrl = import.meta.env.VITE_UMAMI_URL as string | undefined;
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined;
  if (!collectorUrl || !websiteId) return undefined;
  // `authMode`'s own union ('mock' | 'telegram') is a subtype of the
  // contract-derived AuthModeToken ('mock' | 'oidc' | 'bridge' | 'telegram'), so
  // this is a plain assignment, not a cast — the day this app's own AuthMode
  // widens past what the contract declares, THIS line is where it fails to
  // compile (same pattern `apps/web-ui/src/config.ts` uses).
  //
  // `hostname` — architect ruling `decisions/architect/2026-09-16-umami-hostname-fix-bprime-null-shape-ruling.md`,
  // AMENDMENT 2026-09-18T08:50Z: this shell's own deployed host. mini-app deploys to a DIFFERENT
  // host from web-ui (`miniapp.dev.agnivo.org`, `deploy/helm/values-cloud-dev.yaml` — see the
  // `analytics` docblock above), so it needs its own default, not web-ui's. A prod build overrides
  // via `VITE_APP_HOSTNAME=miniapp.agnivo.org`. Both are in `infra`'s `umami-schema-pin-guard`
  // manifest's `allowed:` set for `website_event.hostname`.
  //
  // ⛔ `envOrDefault`, NOT `??` (architect review, `web!655`): an unset Dockerfile ARG with an
  // empty default still lands in the build env as `""` (Docker injects every declared ARG into
  // subsequent RUN steps), and `""` is not nullish — `??` would ship `hostname: ''`, which
  // collapses to the prohibited `'localhost'` signature in umami-ingest.
  const hostname = envOrDefault(import.meta.env.VITE_APP_HOSTNAME as string | undefined, 'miniapp.dev.agnivo.org');
  return { collectorUrl, websiteId, authMode, hostname };
}

// The two gateway route strings (`/v1/auth/telegram:session`, `:refresh`)
// previously lived here as `tgSessionPath`/`tgRefreshPath` AND were pinned by
// `test/config.test.ts` against the merged proto!126 `google.api.http`
// annotations. Both moved to `packages/api-client/src/client.ts` (the typed
// `auth.telegramSession` / `auth.telegramRefresh` methods), so a single source
// owns the wire routes and the typed contract is enforced at the call site.
// The route-string pin is preserved as a `mock.test.ts` describer
// ("MINIAPP_AUTH wire routes") — same regression value, fewer moving parts.

